import { Round as CloseIcon } from '@manomano-internal/tamagoshi-icons/esm/material/Close';
import classnames from 'classnames';
import {
  useEffect,
  type FunctionComponent,
  type MouseEventHandler,
  type PropsWithChildren,
} from 'react';

import { Button } from '@/core/tamagoshi/Button/Button';
import { Alert as Warning } from '@/core/tamagoshi/icons/Alert';
import { CheckCircle as Success } from '@/core/tamagoshi/icons/CheckCircle';
import { Info } from '@/core/tamagoshi/icons/Info';
import { Warning as Danger } from '@/core/tamagoshi/icons/Warning';
import { Text } from '@/core/tamagoshi/Text/Text';
import { forgeTestId } from '@/core/tamagoshi/utils/forgeTestId';

import styles from './AlertMessage.module.scss';

export enum SEVERITIES {
  danger = 'danger',
  info = 'info',
  success = 'success',
  warning = 'warning',
}

export interface AlertMessageProps {
  /**
   * To define the text of the action
   * When not defined, no action will be displayed.
   */
  actionText?: string;
  /**
   * To define the url of the action
   * If define, the action will be a link, if not, the action will be a button
   */
  actionUrl?: string;
  /**
   * To override the style
   */
  className?: classnames.Argument;
  /**
   * To define the name of the close button.
   * No effect if onClose prop is not sets.
   */
  closeButtonLabel?: string;
  'data-testid'?: string;
  /**
   * Spans the full width of the AlertMessage parent
   */
  fullWidth?: boolean;
  /**
   * To define the message of the Alert. You can use children also.
   */
  message?: string;
  /**
   * To define the function to call on click
   * No effect if actionText is not defined.
   */
  onActionClick?: MouseEventHandler;
  /**
   * To define the function to call to close the alert.
   * When defined, a close button will be displayed.
   * You should provide a value to closeButtonLabel prop also for accessibility purpose.
   */
  onClose?: MouseEventHandler;
  /**
   * To define the severity of the Alert
   */
  severity?: 'danger' | 'info' | 'success' | 'warning';
  /**
   * To define the optional title of the Alert
   */
  title?: string;
  withBorder?: boolean;
  onMounted?: () => void;
  onUnmounted?: () => void;
}

/**
 * @deprecated Use the Alert component instead.
 */
export const AlertMessage: FunctionComponent<
  PropsWithChildren<AlertMessageProps>
> = ({
  actionText,
  actionUrl,
  children,
  className,
  closeButtonLabel,
  fullWidth = false,
  message,
  onActionClick,
  onClose,
  severity = SEVERITIES.info,
  title,
  withBorder = true,
  onMounted,
  onUnmounted,
  ...restProps
}) => {
  useEffect(() => {
    if (onMounted) {
      onMounted();
    }

    return () => {
      if (onUnmounted) {
        onUnmounted();
      }
    };
  }, [onMounted, onUnmounted]);

  const testId = restProps['data-testid'];
  const severityIcon = () => {
    const iconTestId = forgeTestId(testId, 'icon');
    const iconTestValue = testId ? severity : undefined;
    switch (severity) {
      case SEVERITIES.success:
        return (
          <Success
            className={styles.icon}
            data-testid={iconTestId}
            data-testvalue={iconTestValue}
          />
        );
      case SEVERITIES.info:
        return (
          <Info
            className={styles.icon}
            data-testid={iconTestId}
            data-testvalue={iconTestValue}
          />
        );
      case SEVERITIES.warning:
        return (
          <Warning
            className={styles.icon}
            data-testid={iconTestId}
            data-testvalue={iconTestValue}
          />
        );
      case SEVERITIES.danger:
        return (
          <Danger
            className={styles.icon}
            data-testid={iconTestId}
            data-testvalue={iconTestValue}
          />
        );
      default:
        break;
    }
    return null;
  };

  return (
    <div
      role="alert"
      className={classnames(
        styles.root,
        styles[severity],
        {
          [styles.fullWidth]: fullWidth,
          [styles.withBorder]: withBorder,
          [styles.withTitle]: !!title,
        },
        className,
      )}
      {...restProps}
    >
      {severityIcon()}
      <div
        className={styles.message}
        data-testid={forgeTestId(testId, 'content')}
      >
        {title && (
          <Text
            tag="strong"
            type="body1"
            weight="semiBold"
            className={styles.title}
            data-testid={forgeTestId(testId, 'title')}
          >
            {title}
          </Text>
        )}
        {message && <Text tag="p">{message}</Text>}

        {children}
      </div>
      {actionText && (
        <Button
          theme="none"
          variant="none"
          className={styles.action}
          href={actionUrl}
          target={actionUrl ? '_blank' : undefined}
          rel={actionUrl ? 'noreferrer' : undefined}
          onClick={onActionClick}
          testId={forgeTestId(testId, 'action')}
        >
          {actionText}
        </Button>
      )}
      {onClose && (
        <Button
          theme="none"
          variant="none"
          className={styles.close}
          onClick={onClose}
          aria-label={closeButtonLabel}
          title={closeButtonLabel}
          icon={<CloseIcon focusable={false} aria-hidden />}
          testId={forgeTestId(testId, 'close')}
        />
      )}
    </div>
  );
};

AlertMessage.displayName = 'AlertMessage';
